'use client';

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  ReactElement,
} from 'react';
import { IntlProvider } from 'use-intl';
import defaultEnMessages from './locales/en.json';
import defaultZhMessages from './locales/zh.json';
import { Locale, DEFAULT_LOCALE } from './config';

// 定义消息结构类型：按语言划分，每个语言对应键值对消息
type Messages = Record<string, string | Record<string, string>>;
type LocaleMessagesMap = Record<Locale, Messages>;

// 组件库默认翻译资源映射（类型精确化，避免any）
const defaultMessagesMap: LocaleMessagesMap = {
  en: defaultEnMessages,
  zh: defaultZhMessages,
};

// 上下文类型定义（明确化命名）
type I18nContextType = {
  /**
   * 切换组件库语言
   * @param locale 目标语言
   * @param customMessages 可选：新语言对应的自定义消息（覆盖默认）
   */
  changeLocale: (
    locale: Locale,
    customMessages?: Partial<LocaleMessagesMap>
  ) => void;
};

// 创建上下文（添加默认值类型提示）
const I18nContext = createContext<I18nContextType | undefined>(undefined);

// Provider属性类型（明确化自定义消息结构）
interface I18nProviderProps {
  children: ReactNode;
  /** 初始默认语言（默认为DEFAULT_LOCALE） */
  defaultLocale?: Locale;
  /** 自定义翻译消息（按语言覆盖默认资源） */
  customMessages?: Partial<LocaleMessagesMap>;
}

/**
 * 组件库专属国际化Provider
 * 用于隔离组件库与宿主应用的国际化配置，管理独立的语言和翻译资源
 */
export function I18nProvider({
  children,
  defaultLocale = DEFAULT_LOCALE,
  customMessages = {},
}: I18nProviderProps): ReactElement {
  // 初始化状态：使用函数式初始化避免重复计算
  const [locale, setLocale] = useState<Locale>(() => {
    // 确保初始语言在支持的范围内
    return defaultMessagesMap[defaultLocale] ? defaultLocale : DEFAULT_LOCALE;
  });

  const [messages, setMessages] = useState<Messages>(() => {
    return mergeMessages(locale, customMessages);
  });

  /**
   * 合并默认消息与自定义消息
   * @param currentLocale 当前语言
   * @param customMsgs 自定义消息（宿主应用传入）
   * @returns 合并后的消息对象（自定义消息优先级更高）
   */
  function mergeMessages(
    currentLocale: Locale,
    customMsgs: Partial<LocaleMessagesMap>
  ): Messages {
    // 确保默认消息存在（若当前语言不支持，fallback到默认语言）
    const baseLocale = defaultMessagesMap[currentLocale]
      ? currentLocale
      : DEFAULT_LOCALE;
    const defaultMsgs = defaultMessagesMap[baseLocale] || {};

    // 合并自定义消息（仅取当前语言的自定义部分）
    const customMsgsForLocale = customMsgs[baseLocale] || {};
    return { ...defaultMsgs, ...customMsgsForLocale };
  }

  /**
   * 切换语言并更新消息
   * @param newLocale 目标语言
   * @param newCustomMessages 可选：新的自定义消息（若有）
   */
  const changeLocale: I18nContextType['changeLocale'] = (
    newLocale,
    newCustomMessages
  ) => {
    // 校验目标语言是否支持，不支持则用默认语言
    const validLocale = defaultMessagesMap[newLocale]
      ? newLocale
      : DEFAULT_LOCALE;

    // 更新语言和消息（优先使用新传入的自定义消息，否则沿用之前的）
    setLocale(validLocale);
    setMessages(
      mergeMessages(validLocale, newCustomMessages || customMessages)
    );
  };

  return (
    <I18nContext.Provider value={{ changeLocale }}>
      {/* 传递给use-intl的Provider，使用组件库独立的语言和消息 */}
      <IntlProvider locale={locale} messages={messages} timeZone="UTC">
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  );
}

/**
 * 获取组件库国际化上下文（包含语言切换方法）
 * 必须在I18nProvider内部使用，否则会抛出错误
 */
export function useI18nContext() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18nContext must be used within I18nProvider');
  }
  return context;
}
